import type { AiSettingsView } from "../../../server/core/ai";
import { api, el, toast } from "../../ui/dom";
import { icon } from "../../ui/icons";
import { chip, table } from "../../ui/record";
import { pageHead } from "../empty";
import type { ScreenContext } from "../../shell/types";

// Settings › AI (brief 1B-8, mockup v14 "settings/ai"): AI for the whole company; What AI may do,
// with a switch per feature (Dictation, Writing help and Reading uploads are live; the rest say
// "Comes later" and stay off); Rules AI follows (the first is fixed); Budget (monthly limit, warn
// %, what happens at the limit, this month spent of the limit); Which model does which job with
// this month's spend per job; the price rows the spend is worked out from; and "With AI off".
// Admin only; every change saves as it's made and is in the history.

type View = AiSettingsView;

const STATE_WORD: Record<string, [string, string]> = {
  on: ["On", "ok"],
  paused: ["Paused until next month", "warn"],
  not_set_up: ["Not set up yet", "warn"],
  off: ["Off", "plain"],
};

export async function aiScreen(host: HTMLElement, ctx: ScreenContext) {
  host.append(
    pageHead(
      ctx,
      el(
        "span",
        {},
        "Turn AI on or off for ",
        el("b", {}, ctx.me.company.name),
        ", choose what it may do, and cap the spend.",
      ),
      [],
      "Company settings",
    ),
  );
  const res = await api<View>("GET", "/api/settings/ai");
  if (!res.ok) {
    host.append(
      el(
        "p",
        { class: "note", role: "alert" },
        res.body.error ?? "AI settings couldn't be loaded.",
      ),
    );
    return;
  }
  let view = res.body;
  const page = el("div", {});
  host.append(page);

  /** Saves a change; the buttons on every screen follow (the menus and /api/me are asked again). */
  async function save(body: Record<string, unknown>, said: string, sub?: string): Promise<boolean> {
    const put = await api<View>("PUT", "/api/settings/ai", body);
    if (!put.ok) {
      toast("That wasn't saved", put.body.error);
      draw();
      return false;
    }
    view = put.body;
    await ctx.refresh();
    draw();
    toast(said, sub);
    return true;
  }

  function sw(id: string, on: boolean, label: string, change: (on: boolean) => void) {
    const input = el("input", { type: "checkbox", id, "aria-label": label, checked: on });
    input.addEventListener("change", () => {
      input.disabled = true;
      change(input.checked);
    });
    return el("label", { class: "switch-l" }, input, el("span", {}, on ? "On" : "Off"));
  }

  function master() {
    return el(
      "section",
      { class: `card aimaster${view.on ? "" : " off"}`, "aria-label": "AI for the whole company" },
      el(
        "div",
        { class: "cardhead", style: "margin:0" },
        el(
          "div",
          {},
          el("h2", {}, icon("cpu"), " AI for the whole company"),
          el(
            "span",
            { class: "small muted" },
            view.on
              ? "On. Each feature below can still be switched off on its own."
              : "Off. Nothing is sent to any AI model. Dictation, writing help and reading uploads are hidden everywhere.",
          ),
        ),
        sw(
          "aiall",
          view.on,
          "AI for the whole company on",
          (on) =>
            void save(
              { on },
              `AI is ${on ? "on" : "off"}`,
              on ? "Its buttons are back" : "Every AI button is gone; work goes on by hand",
            ),
        ),
      ),
    );
  }

  function features() {
    return el(
      "section",
      { class: "card", "aria-label": "What AI may do" },
      el(
        "div",
        { class: "cardhead" },
        el("h2", {}, "What AI may do"),
        el(
          "span",
          { class: "small muted" },
          "Switch a feature off and its buttons disappear from every screen",
        ),
      ),
      table<View["features"][number]>(
        [
          {
            h: "Feature",
            f: (r) =>
              el("span", {}, el("b", {}, r.name), el("div", { class: "small muted" }, r.blurb)),
          },
          { h: "Where it shows", f: (r) => r.where },
          {
            h: "",
            f: (r) => {
              if (!r.live) return chip("Comes later", "plain");
              if (!view.on) return chip("Off (AI is off)", "plain");
              const s = sw(
                `aif-${r.key}`,
                r.on,
                `${r.name} on`,
                (on) =>
                  void save({ features: { [r.key]: on } }, `${r.name} is ${on ? "on" : "off"}`),
              );
              const [word, level] = STATE_WORD[r.state] ?? ["", "plain"];
              return el(
                "span",
                { class: "row", style: "gap:8px;flex-wrap:wrap" },
                s,
                r.on && r.state !== "on" ? chip(word, level) : null,
              );
            },
          },
        ],
        view.features,
        undefined,
        "What AI may do",
      ),
    );
  }

  function rules() {
    return el(
      "section",
      { class: "card", "aria-label": "Rules AI follows" },
      el("h2", {}, icon("shield-lock"), " Rules AI follows"),
      el(
        "div",
        { class: "form" },
        ...view.rules.map((r) => {
          const box = el("input", {
            type: "checkbox",
            id: `aip-${r.key}`,
            checked: r.on,
            disabled: !!r.fixed,
          });
          if (!r.fixed)
            box.addEventListener("change", () => {
              box.disabled = true;
              void save({ rules: { [r.key]: box.checked } }, "Rule saved", r.label);
            });
          return el(
            "label",
            { class: "ptask" },
            box,
            ` ${r.label}`,
            r.fixed ? el("span", { class: "small muted" }, " (fixed)") : null,
          );
        }),
      ),
    );
  }

  function budget() {
    const b = view.budget;
    const limit = el("input", {
      id: "aib1",
      type: "number",
      min: "0",
      step: "1",
      value: String(b.limit),
    });
    const warn = el("input", {
      id: "aib2",
      type: "number",
      min: "1",
      max: "100",
      value: String(b.warnPct),
    });
    const at = el(
      "select",
      { id: "aib3" },
      el(
        "option",
        { value: "pause", selected: b.atLimit === "pause" },
        "Pause AI until next month (work goes on by hand)",
      ),
      el(
        "option",
        { value: "keep_going", selected: b.atLimit === "keep_going" },
        "Keep going and alert me",
      ),
    );
    limit.addEventListener(
      "change",
      () => void save({ budget: limit.value }, "Monthly limit saved"),
    );
    warn.addEventListener(
      "change",
      () => void save({ warnPct: Number(warn.value) }, "Warning saved"),
    );
    at.addEventListener(
      "change",
      () => void save({ atLimit: at.value }, "Saved", at.selectedOptions[0]?.text),
    );
    return el(
      "section",
      { class: "card", "aria-label": "Budget" },
      el("h2", {}, icon("credit-card"), " Budget"),
      el(
        "div",
        { class: "grid2", style: "gap:10px" },
        el("div", { class: "field" }, el("label", { for: "aib1" }, "Monthly limit ($)"), limit),
        el("div", { class: "field" }, el("label", { for: "aib2" }, "Warn me at (%)"), warn),
      ),
      el("div", { class: "field" }, el("label", { for: "aib3" }, "When the limit is reached"), at),
      el(
        "p",
        { class: "small", id: "aispent" },
        "This month: ",
        el("b", {}, b.spent),
        ` of ${b.limitText} · ${b.pct}%`,
      ),
      el(
        "div",
        {
          class: "meter",
          role: "progressbar",
          "aria-label": "This month's AI spend",
          "aria-valuenow": String(Math.min(100, b.pct)),
          "aria-valuemin": "0",
          "aria-valuemax": "100",
        },
        el("span", { style: `width:${Math.min(100, b.pct)}%` }),
      ),
    );
  }

  function models() {
    return el(
      "section",
      { class: "card", "aria-label": "Which model does which job" },
      el(
        "div",
        { class: "cardhead" },
        el("h2", {}, icon("ai-model"), " Which model does which job"),
        el(
          "span",
          { class: "small muted" },
          "Cheaper models for sorting, stronger ones for clinical writing",
        ),
      ),
      table<View["jobs"][number]>(
        [
          { h: "Job", f: (r) => el("b", {}, r.name) },
          {
            h: "Model",
            f: (r) => {
              if (!r.live) return chip("Comes later", "plain");
              const s = el(
                "select",
                { "aria-label": `Model for ${r.name}` },
                ...r.models.map((m) =>
                  el("option", { value: m.id, selected: m.id === r.model }, m.label),
                ),
                el("option", { value: "", disabled: true }, `${r.ownKey} (comes later)`),
              );
              s.addEventListener(
                "change",
                () =>
                  void save(
                    { models: { [r.key]: s.value } },
                    "Model saved",
                    `${r.name}: ${s.selectedOptions[0]?.text ?? ""}`,
                  ),
              );
              return el(
                "span",
                { class: "row", style: "gap:8px;flex-wrap:wrap" },
                s,
                r.setUp ? null : chip("Not set up yet", "warn"),
              );
            },
          },
          { h: "This month", num: true, f: (r) => (r.live ? r.spent : "–") },
        ],
        view.jobs,
        undefined,
        "Which model does which job",
      ),
    );
  }

  function prices() {
    const money = (v: number) => (Math.round(v * 1_000_000) / 1_000_000).toString();
    return el(
      "section",
      { class: "card", "aria-label": "Prices" },
      el(
        "div",
        { class: "cardhead" },
        el("h2", {}, icon("credit-card"), " Prices"),
        el(
          "span",
          { class: "small muted" },
          "What each call costs is worked out from these. Change them when a provider's price changes.",
        ),
      ),
      table<View["prices"][number]>(
        [
          { h: "Model", f: (r) => el("b", {}, r.label) },
          ...(
            [
              ["inputPerMillion", "Per million tokens in ($)"],
              ["outputPerMillion", "Per million tokens out ($)"],
              ["audioPerMinute", "Per audio minute ($)"],
            ] as const
          ).map(([k, h]) => ({
            h,
            f: (r: View["prices"][number]) => {
              const i = el("input", {
                type: "number",
                min: "0",
                step: "any",
                value: money(r[k]),
                "aria-label": `${r.label}: ${h}`,
                style: "max-width:120px",
              });
              i.addEventListener("change", async () => {
                const put = await api<View>("PUT", `/api/settings/ai/prices/${r.id}`, {
                  [k]: i.value,
                });
                if (!put.ok) return toast("That price wasn't saved", put.body.error);
                view = put.body;
                draw();
                toast("Price saved", r.label);
              });
              return i;
            },
          })),
          {
            h: "As of",
            f: (r) => {
              const i = el("input", {
                type: "date",
                value: r.asOf,
                "aria-label": `${r.label}: as of`,
              });
              i.addEventListener("change", async () => {
                const put = await api<View>("PUT", `/api/settings/ai/prices/${r.id}`, {
                  asOf: i.value,
                });
                if (!put.ok) return toast("That date wasn't saved", put.body.error);
                view = put.body;
                draw();
              });
              return i;
            },
          },
        ],
        view.prices,
        undefined,
        "Prices",
      ),
    );
  }

  function draw() {
    page.replaceChildren(
      master(),
      features(),
      el("div", { class: "grid2" }, rules(), budget()),
      models(),
      prices(),
      el(
        "div",
        { class: "note" },
        el(
          "p",
          {},
          el("b", {}, "With AI off:"),
          " uploads are filled in by hand and the inbox isn't sorted.",
        ),
      ),
    );
  }
  draw();
}
